import { ArrowUpRight, CircleHelp, Info, ShieldAlert, Wrench } from "lucide-react";
import { type ReactNode, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import type { NotableKind, SummaryNotable, SummaryRisk } from "@/lib/archviz/level-summary";
import { summarize } from "@/lib/archviz/level-summary";
import { cn } from "@/lib/utils";
import { useArchViz } from "./ArchVizContext";

type SummaryTab = "general" | "risks" | "notable";

const SEVERITY_DOT_CLASS: Record<SummaryRisk["severity"], string> = {
  critical: "bg-danger",
  warning: "bg-warning",
  info: "bg-ink-subtle",
};

const SEVERITY_ORDER: SummaryRisk["severity"][] = ["critical", "warning", "info"];

const SEVERITY_SECTION_LABEL: Record<SummaryRisk["severity"], string> = {
  critical: "Critical",
  warning: "Warning",
  info: "Info",
};

const NOTABLE_ICON: Record<NotableKind, typeof Info> = {
  assumption: Info,
  question: CircleHelp,
  "ops-note": Wrench,
  decision: ShieldAlert,
};

const NOTABLE_SECTION_ORDER: NotableKind[] = ["question", "assumption", "ops-note", "decision"];

const NOTABLE_SECTION_LABEL: Record<NotableKind, string> = {
  question: "Open questions",
  assumption: "Unverified",
  "ops-note": "Heads up",
  decision: "Recent decisions",
};

function SummarySection({
  label,
  count,
  children,
}: {
  label: string;
  count: number;
  children: ReactNode;
}) {
  return (
    <section>
      <div className="flex items-baseline gap-1.5 border-b border-border pb-1 mb-1">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          {label}
        </span>
        <span className="text-[10px] text-muted-foreground">· {count}</span>
      </div>
      <ul className="space-y-1 pt-1">{children}</ul>
    </section>
  );
}

function RiskRow({ risk, onGo }: { risk: SummaryRisk; onGo: (nodeId: string | null) => void }) {
  const linkable = Boolean(risk.nodeId);
  const Tag: "button" | "div" = linkable ? "button" : "div";
  const secondary = `${risk.categoryLabel} · ${risk.response ?? "No mitigation yet."}`;
  return (
    <li>
      <Tag
        type={linkable ? "button" : undefined}
        onClick={linkable ? () => onGo(risk.nodeId) : undefined}
        className={cn(
          "w-full text-left flex items-start gap-3 px-2 py-2 rounded",
          linkable && "hover:bg-muted/50 cursor-pointer group",
        )}
        aria-label={linkable ? "Open in detail panel" : undefined}
      >
        <span
          className={cn("mt-1.5 h-2 w-2 rounded-full shrink-0", SEVERITY_DOT_CLASS[risk.severity])}
          role="img"
          aria-label={risk.severity}
        />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-foreground">{risk.description}</div>
          <div className="text-xs text-muted-foreground mt-0.5">{secondary}</div>
        </div>
        {linkable && (
          <ArrowUpRight className="h-3.5 w-3.5 mt-1 text-muted-foreground/60 group-hover:text-foreground shrink-0" />
        )}
      </Tag>
    </li>
  );
}

function NotableRow({
  item,
  onGo,
}: {
  item: SummaryNotable;
  onGo: (nodeId: string | null) => void;
}) {
  const linkable = Boolean(item.nodeId);
  const Icon = NOTABLE_ICON[item.kind];
  const Tag: "button" | "div" = linkable ? "button" : "div";
  return (
    <li>
      <Tag
        type={linkable ? "button" : undefined}
        onClick={linkable ? () => onGo(item.nodeId) : undefined}
        className={cn(
          "w-full text-left flex items-start gap-3 px-2 py-2 rounded",
          linkable && "hover:bg-muted/50 cursor-pointer group",
        )}
        aria-label={linkable ? "Open in detail panel" : undefined}
      >
        <Icon className="h-3.5 w-3.5 mt-0.5 text-muted-foreground shrink-0" />
        <span className="min-w-0 flex-1 text-sm text-foreground/90">{item.text}</span>
        {linkable && (
          <ArrowUpRight className="h-3.5 w-3.5 mt-0.5 text-muted-foreground/60 group-hover:text-foreground shrink-0" />
        )}
      </Tag>
    </li>
  );
}

interface LevelSummaryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function LevelSummaryDialog({ open, onOpenChange }: LevelSummaryDialogProps) {
  const { state, dispatch } = useArchViz();
  const [tab, setTab] = useState<SummaryTab>("general");

  const summary = useMemo(() => {
    if (!state.architecture) return null;
    return summarize(state.architecture, state.drillLevel, state.selectedContainerId);
  }, [state.architecture, state.drillLevel, state.selectedContainerId]);

  if (!summary) return null;

  const { title, collapsedSentence, expandedNarrative, inventory, riskCounts, risks, notable } =
    summary;
  const inventoryCount = inventory.reduce((acc, g) => acc + g.chips.length, 0);
  const riskCount = risks.length;
  const notableCount = notable.length;

  const goToNode = (nodeId: string | null, forRisks: boolean) => {
    if (!nodeId) return;
    onOpenChange(false);
    if (forRisks) {
      dispatch({ type: "SELECT_NODE_FOR_RISKS", nodeId });
    } else {
      dispatch({ type: "SELECT_NODE", nodeId });
    }
  };

  const tabs: { id: SummaryTab; label: string; count?: number }[] = [
    { id: "general", label: "General" },
    { id: "risks", label: "Risks", count: riskCount },
    { id: "notable", label: "Notable", count: notableCount },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-base">{title}</DialogTitle>
        </DialogHeader>

        <div className="flex items-center gap-1 border-b border-border -mx-6 px-6">
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={cn(
                "px-3 py-2 text-sm border-b-2 -mb-px transition-colors",
                tab === t.id
                  ? "border-foreground text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground",
              )}
            >
              {t.label}
              {typeof t.count === "number" && (
                <span className="ml-1.5 text-[10px] text-muted-foreground">{t.count}</span>
              )}
            </button>
          ))}
        </div>

        <div className="overflow-y-auto pt-4 -mx-6 px-6">
          {tab === "general" && (
            <div className="space-y-4 text-sm">
              {collapsedSentence && <p className="text-muted-foreground">{collapsedSentence}</p>}

              {expandedNarrative && <p className="leading-relaxed">{expandedNarrative}</p>}

              <div className="flex flex-wrap gap-2">
                {inventoryCount > 0 && (
                  <div className="rounded-md border border-border bg-muted/40 px-3 py-1.5">
                    <div className="text-sm font-semibold text-foreground tabular-nums">
                      {inventoryCount}
                    </div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                      {inventoryCount === 1 ? "component" : "components"}
                    </div>
                  </div>
                )}
                {riskCounts.critical > 0 && (
                  <button
                    type="button"
                    onClick={() => setTab("risks")}
                    aria-label={`View ${riskCounts.critical} critical ${riskCounts.critical === 1 ? "risk" : "risks"}`}
                    className="rounded-md border border-border bg-muted/40 px-3 py-1.5 text-left transition-colors hover:bg-muted"
                  >
                    <div className="text-sm font-semibold text-danger tabular-nums">
                      {riskCounts.critical}
                    </div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                      critical
                    </div>
                  </button>
                )}
                {riskCounts.warning > 0 && (
                  <button
                    type="button"
                    onClick={() => setTab("risks")}
                    aria-label={`View ${riskCounts.warning} warning ${riskCounts.warning === 1 ? "risk" : "risks"}`}
                    className="rounded-md border border-border bg-muted/40 px-3 py-1.5 text-left transition-colors hover:bg-muted"
                  >
                    <div className="text-sm font-semibold text-warning tabular-nums">
                      {riskCounts.warning}
                    </div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                      warning
                    </div>
                  </button>
                )}
                {riskCounts.critical === 0 && riskCounts.warning === 0 && (
                  <span className="self-center text-xs text-muted-foreground">
                    No risks logged.
                  </span>
                )}
              </div>

              {inventory.length > 0 && (
                <section>
                  <h4 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">
                    What's at this level
                  </h4>
                  <div className="space-y-1.5">
                    {inventory.map((g, gi) => (
                      <div key={g.groupLabel ?? `group-${gi}`}>
                        {g.groupLabel && (
                          <div className="text-[11px] text-muted-foreground mb-0.5">
                            {g.groupLabel}
                          </div>
                        )}
                        <div className="flex flex-wrap gap-1">
                          {g.chips.map((chip) => (
                            <span
                              key={chip}
                              className="text-xs px-1.5 py-0.5 rounded bg-muted text-foreground/90"
                            >
                              {chip}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              )}
            </div>
          )}

          {tab === "risks" && (
            <div className="space-y-4">
              {risks.length === 0 ? (
                <p className="text-sm text-muted-foreground">No risks logged at this level.</p>
              ) : (
                SEVERITY_ORDER.map((sev) => {
                  const group = risks.filter((r) => r.severity === sev);
                  if (group.length === 0) return null;
                  return (
                    <SummarySection
                      key={sev}
                      label={SEVERITY_SECTION_LABEL[sev]}
                      count={group.length}
                    >
                      {group.map((r) => (
                        <RiskRow
                          key={`${r.severity}-${r.categoryLabel}-${r.description}`}
                          risk={r}
                          onGo={(nodeId) => goToNode(nodeId, true)}
                        />
                      ))}
                    </SummarySection>
                  );
                })
              )}
            </div>
          )}

          {tab === "notable" && (
            <div className="space-y-4">
              {notable.length === 0 ? (
                <p className="text-sm text-muted-foreground">Nothing notable at this level.</p>
              ) : (
                NOTABLE_SECTION_ORDER.map((kind) => {
                  const group = notable.filter((n) => n.kind === kind);
                  if (group.length === 0) return null;
                  return (
                    <SummarySection
                      key={kind}
                      label={NOTABLE_SECTION_LABEL[kind]}
                      count={group.length}
                    >
                      {group.map((n) => (
                        <NotableRow
                          key={`${n.kind}-${n.text}`}
                          item={n}
                          onGo={(nodeId) => goToNode(nodeId, false)}
                        />
                      ))}
                    </SummarySection>
                  );
                })
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
